import { Box, Button, Dialog, DialogActions, Stack, Typography } from "@mui/material";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { blockMasterColumnsObj } from "../data";
import { inputTypes, selectOptionsKeyName, arrType } from "data/globalData";
import { useEffect, useState } from "react";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { attrValAssocObj } from "components/PimDashboard/DashboardComponents/Designer/DesignerAttributes/data";

const CreateOrUpdateBlockModal = ({ open, setOpen, handleSaveFunction = () => {}, mode = "create", selectedRow, setSelectedRow }) => {
	const { snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const { allAttributes } = PimDashboardState();

	const sectionAttr = allAttributes?.find((attr) => attr.key === "gender");
	const brandAttr = allAttributes?.find((attr) => attr.key === "brand");
	const categoryAttr = allAttributes?.find((attr) => attr.key === "category");
	const sizesAttr = allAttributes?.find((attr) => attr.key === "sizes");

	const defPageData = Object.values(blockMasterColumnsObj)
		.map((col) => ({ [col.key]: getColDefaultValue(col) }))
		.reduce((acc, row) => ({ ...acc, ...row }), {});

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});

	useEffect(() => {
		if (mode === "update" && selectedRow) {
			setPageData(selectedRow);
		}
	}, [selectedRow, mode]);

	const handleCloseModal = () => {
		setOpen(false);
		setErrorObj({});
		setPageData(defPageData);
		setSelectedRow();
	};

	const handleSaveClick = () => {
		let errObj = {};

		Object.values(blockMasterColumnsObj).forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: `Please fill the required fields`,
			});
			setErrorObj(errObj);
			return;
		}

		setErrorObj({});
		if (typeof handleSaveFunction === "function") handleSaveFunction(pageData);
	};

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={handleCloseModal} scroll="body">
			<Stack width="100%" p={2} boxSizing="border-box" spacing={2}>
				<Typography variant="h6">{mode === "create" ? "Create" : "Update"} Block Master</Typography>

				<Box display="flex" width="100%" rowGap={2} flexWrap="wrap" columnGap="3%">
					<RenderInputField
						column={blockMasterColumnsObj.block_code}
						data={pageData}
						setData={() => {}}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.block_code.key]}
					/>
					<RenderInputField
						column={{
							...blockMasterColumnsObj.brand,
							[selectOptionsKeyName]: {
								type: arrType,
								list: brandAttr?.[attrValAssocObj] || [],
								getOptionLabel: (item) => ({ name: item.value, key: item.value }),
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.brand.key]}
						disabled={mode === "update"}
					/>
					<RenderInputField
						column={{
							...blockMasterColumnsObj.section,
							[selectOptionsKeyName]: {
								type: arrType,
								list: sectionAttr?.[attrValAssocObj] || [],
								getOptionLabel: (item) => ({ name: item.value, key: item.value }),
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.section.key]}
					/>
					<RenderInputField
						column={{
							...blockMasterColumnsObj.department,
							[selectOptionsKeyName]: {
								type: arrType,
								list: categoryAttr?.[attrValAssocObj] || [],
								getOptionLabel: (item) => ({ name: item.value, key: item.value }),
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.department.key]}
					/>
					<RenderInputField
						column={blockMasterColumnsObj.block_name}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.block_name.key]}
						disabled={mode === "update"}
					/>
					<RenderInputField
						column={{
							...blockMasterColumnsObj.size,
							input_type: inputTypes.SELECT.key,
							[selectOptionsKeyName]: {
								type: arrType,
								list: sizesAttr?.[attrValAssocObj] || [],
								getOptionLabel: (item) => ({ name: item.value, key: item.value }),
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.size.key]}
						disabled={mode === "update"}
					/>
					<RenderInputField
						column={blockMasterColumnsObj.chest}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.chest.key]}
					/>
					<RenderInputField
						column={blockMasterColumnsObj.garment_length}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.garment_length.key]}
					/>
					<RenderInputField
						column={blockMasterColumnsObj.shoulder}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.shoulder.key]}
					/>
					<RenderInputField
						column={blockMasterColumnsObj.sleeve_length}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[blockMasterColumnsObj.sleeve_length.key]}
					/>
				</Box>
			</Stack>
			<DialogActions>
				<Button variant="outlined" onClick={handleCloseModal}>
					Cancel
				</Button>
				<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={handleSaveClick}>
					{mode === "create" ? "Create" : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreateOrUpdateBlockModal;
